import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import axios from 'axios';
import { Shield, Check, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [activating, setActivating] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError('No invite token provided'); setVerifying(false); return; }
    axios.get(`${API}/auth/verify-invite/${token}`)
      .then(r => setIdentity(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired invite'))
      .finally(() => setVerifying(false));
  }, [token]);

  const pwValid = password.length >= 8;
  const pwMatch = password === confirm;
  const canSubmit = pwValid && pwMatch && !activating;

  const handleActivate = async () => {
    if (!canSubmit) return;
    setActivating(true);
    try {
      await axios.post(`${API}/auth/activate-staff`, { token, password, confirm_password: confirm });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Activation failed');
    } finally { setActivating(false); }
  };

  // Loading
  if (verifying) return (
    <div className="min-h-screen bg-[#FBFaf9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" />
    </div>
  );

  // Error
  if (error && !identity) return (
    <div className="min-h-screen bg-[#FBFaf9] flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center">
        <AlertCircle className="w-12 h-12 text-[#C87B7B] mx-auto mb-4" strokeWidth={1} />
        <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29] mb-2">Unable to Activate</h1>
        <p className="text-sm text-[#8A8681] font-['Work_Sans'] mb-6">{error}</p>
        <Button variant="outline" onClick={() => navigate('/login')} className="border-[#EBE8E3] text-[#5C5955]">Go to Login</Button>
      </div>
    </div>
  );

  // Success
  if (success) return (
    <div className="min-h-screen bg-[#FBFaf9] flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center">
        <div className="w-14 h-14 rounded-full bg-[#4E7A6B]/10 flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-[#4E7A6B]" />
        </div>
        <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29] mb-2">Account Activated</h1>
        <p className="text-sm text-[#8A8681] font-['Work_Sans'] mb-6">Your password has been set. You can now log in.</p>
        <Button onClick={() => navigate('/login')} className="bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full px-8" data-testid="go-to-login">
          Go to Login
        </Button>
      </div>
    </div>
  );

  // Set password form
  return (
    <div className="min-h-screen bg-[#FBFaf9] flex items-center justify-center px-4">
      <div className="max-w-sm w-full" data-testid="activate-page">
        <div className="text-center mb-8">
          <Shield className="w-10 h-10 text-[#8C6B55] mx-auto mb-3" strokeWidth={1} />
          <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29] mb-1">Welcome, {identity?.name}</h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans']">
            You've been invited as <strong className="text-[#5C5955] capitalize">{identity?.role?.replace('_', ' ')}</strong>
          </p>
          <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-2">Set your password to activate your account</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#5C5955] font-['Work_Sans'] mb-1.5 block">Password</label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="bg-white border-[#EBE8E3] text-[#2C2A29] pr-10"
                data-testid="activate-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8681]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {password && !pwValid && <p className="text-[10px] text-[#C87B7B] mt-1">Must be at least 8 characters</p>}
            {password && pwValid && <p className="text-[10px] text-[#4E7A6B] mt-1">Password strength OK</p>}
          </div>

          <div>
            <label className="text-xs text-[#5C5955] font-['Work_Sans'] mb-1.5 block">Confirm Password</label>
            <Input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat password"
              className="bg-white border-[#EBE8E3] text-[#2C2A29]"
              data-testid="activate-confirm"
            />
            {confirm && !pwMatch && <p className="text-[10px] text-[#C87B7B] mt-1">Passwords do not match</p>}
          </div>

          {error && <p className="text-xs text-[#C87B7B] text-center">{error}</p>}

          <Button
            onClick={handleActivate}
            disabled={!canSubmit}
            className="w-full bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full py-5 mt-2"
            data-testid="activate-submit"
          >
            {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate Account'}
          </Button>
        </div>

        <p className="text-[10px] text-[#8A8681] text-center mt-6 font-['Work_Sans']">ReGenflō Care OS</p>
      </div>
    </div>
  );
}
